import * as React from "react"
import { DataGrid } from "../../ui/data-grid"
import { ColDef, RowClassParams } from "ag-grid-community"
import { DateTime } from 'luxon'
import { CalendarIcon, AlertTriangleIcon } from 'lucide-react'
import { Button } from '../../ui/button'
import { Popover, PopoverContent, PopoverTrigger } from '../../ui/popover'
import { Tooltip, TooltipContent, TooltipTrigger } from '../../ui/tooltip'
import { Calendar } from '../../ui/calendar'
import { cn } from '../../../lib/utils'
import { formatDate } from '../../../utils/format'
import supabasePromotionAnalysisService from "../../../lib/supabase/formats/supabasePromotionAnalysisService"
import { useModels } from "../../../lib/supabase/models/supabaseModelService"
import SupabaseFanGainService from '../../../lib/supabase/fan-gain/supabaseFanGainService'
import { Label } from "../../ui/label"

type EnrichedRowData = {
  modelName: string
  fan_gain: number
  fan_gain_per_ad: number
  mainGroup: string
  model_id: string
  int_promos: number
  cross_promos: number
  total: number
  group_name: string
  [key: string]: any
}

// Hook to load fan gain data
const useFanGainData = (dateRange: { from: DateTime, to: DateTime } | undefined, modelsLoading: boolean) => {
  const [dailyFanGainData, setDailyFanGainData] = React.useState<{ [model_id: string]: any }>({})
  const [fanGainGGData, setFanGainGGData] = React.useState<{ [model_id: string]: any }>({})
  const [isLoading, setIsLoading] = React.useState(true)

  React.useEffect(() => {
    const loadFanGainData = async () => {
      if (modelsLoading || !dateRange?.from || !dateRange?.to) return

      setIsLoading(true)

      try {
        const fromDate = dateRange.from
        const toDate = dateRange.to

        // Collect data for all months in the range
        const allDailyData: { [model_id: string]: any } = {}
        const allGGData: { [model_id: string]: any } = {}

        let currentDate = fromDate.startOf('month')
        while (currentDate <= toDate.startOf('month')) {
          const month = currentDate.month
          const year = currentDate.year

          try {
            // Load both daily fan gain data and GG swap data in parallel
            const [fanGainEntries, ggSwapEntries] = await Promise.all([
              SupabaseFanGainService.getEntriesForMonth(year, month, 'fan-gain'),
              SupabaseFanGainService.getEntriesForMonth(year, month, 'gg-swaps')
            ])

            // Transform fan gain entries into expected structure
            fanGainEntries.forEach(entry => {
              if (!allDailyData[entry.model_id]) {
                allDailyData[entry.model_id] = { inputValues: {} }
              }

              const entryDate = DateTime.fromISO(entry.date)
              const dayKey = `day${entryDate.day}`
              allDailyData[entry.model_id].inputValues[dayKey] = entry.value
            })

            // Transform GG swap entries into expected structure
            ggSwapEntries.forEach(entry => {
              if (!allGGData[entry.model_id]) {
                allGGData[entry.model_id] = { dailyValues: {} }
              }

              const entryDate = DateTime.fromISO(entry.date)
              const dayKey = `day${entryDate.day}`
              allGGData[entry.model_id].dailyValues[dayKey] = entry.value
            })

          } catch (error) {
            console.error(`Error loading data for ${month}/${year}:`, error)
          }

          currentDate = currentDate.plus({ month: 1 })
        }

        setDailyFanGainData(allDailyData)
        setFanGainGGData(allGGData)
      } catch (error) {
        console.error('Error loading fan gain data:', error)
      } finally {
        setIsLoading(false)
      }
    }

    loadFanGainData()
  }, [dateRange?.from, dateRange?.to, modelsLoading])

  return { dailyFanGainData, fanGainGGData, isLoading }
}

// Hook to load real promo data using new Supabase service
const usePromoData = (dateRange: { from: DateTime, to: DateTime } | undefined, models: any[]) => {
  const [data, setData] = React.useState<any[]>([])
  const [loading, setLoading] = React.useState(true)
  const [error, setError] = React.useState<string | null>(null)

  React.useEffect(() => {
    const loadData = async () => {
      if (!dateRange?.from || !dateRange?.to || !models) return

      try {
        setLoading(true)

        // Get promotion analysis from new service
        const promotionAnalysis = await supabasePromotionAnalysisService.analyzePromotions(
          dateRange.from,
          dateRange.to
        )

        // Transform the nested data into the flat structure expected by the component
        const flattenedData: any[] = []
        const modelGroups = new Map(models.map(model => [model.id, model.scheduling_group]))

        // Process the promotion analysis data to create sub-group entries for each model
        Object.entries(promotionAnalysis).forEach(([promotingGroup, promotedGroups]) => {
          Object.entries(promotedGroups).forEach(([promotedGroup, promotedModels]) => {
            Object.entries(promotedModels).forEach(([modelId, promotions]) => {
              const promoCount = promotions.length
              const modelMainGroup = modelGroups.get(modelId) || 'Unknown'

              // Classify as internal or cross promo based on group matching
              const isInternal = promotingGroup === promotedGroup

              // Create a sub-group entry for this promoting group -> model relationship
              flattenedData.push({
                model_id: modelId,
                int_promos: isInternal ? promoCount : 0,
                cross_promos: isInternal ? 0 : promoCount,
                total: promoCount,
                mainGroup: modelMainGroup,
                group_name: promotingGroup // This is the promoting group (A, B, C, etc.)
              })
            })
          })
        })

        // Sort by mainGroup, then by model_id to group sub-groups under each model
        const sortedData = flattenedData.sort((a, b) => {
          if (a.mainGroup !== b.mainGroup) {
            return a.mainGroup.localeCompare(b.mainGroup)
          }
          if (a.model_id !== b.model_id) {
            return a.model_id.localeCompare(b.model_id)
          }
          return a.group_name.localeCompare(b.group_name)
        })

        setData(sortedData)
      } catch (err) {
        setError(err instanceof Error ? err.message : 'Failed to load promo data')
        console.error('Error loading promo data:', err)
      } finally {
        setLoading(false)
      }
    }

    loadData()
  }, [dateRange?.from, dateRange?.to, models])

  return { data, loading, error }
}

export const FanGainPerAdSpreadsheet: React.FC = () => {
  // Date range state - default to current month (same as original component)
  const [dateRange, setDateRange] = React.useState<{ from: DateTime, to: DateTime } | undefined>(() => {
    const now = DateTime.now().setZone('Europe/London')
    const startOfMonth = now.startOf('month')
    const dayBefore = now.minus({ days: 1 })

    const fromDate = dayBefore < startOfMonth ? dayBefore : startOfMonth

    // Create UTC dates at midnight to match manual selection behavior
    return {
      from: DateTime.fromObject({ year: fromDate.year, month: fromDate.month, day: fromDate.day }, { zone: 'UTC' }),
      to: DateTime.fromObject({ year: dayBefore.year, month: dayBefore.month, day: dayBefore.day }, { zone: 'UTC' })
    }
  })

  // Temporary date range for preview in calendar
  const [tempDateRange, setTempDateRange] = React.useState<{ from: DateTime, to: DateTime } | undefined>(dateRange)

  // Popover open state
  const [isPopoverOpen, setIsPopoverOpen] = React.useState(false)

  // Adjust date range for same-day selections to cover full day (for promo data)
  const adjustedDateRange = React.useMemo(() => {
    if (!dateRange?.from || !dateRange?.to) return dateRange

    // If selecting the same day, use start and end of that day
    if (dateRange.from.toMillis() === dateRange.to.toMillis()) {
      const selectedDate = dateRange.from;
      return {
        from: selectedDate.startOf('day'),
        to: selectedDate.endOf('day')
      }
    }

    return dateRange
  }, [dateRange])

  // Fan gain date range - always includes one day before for calculations
  const fanGainDateRange = React.useMemo(() => {
    if (!adjustedDateRange?.from || !adjustedDateRange?.to) return adjustedDateRange

    const fromDate = adjustedDateRange.from.minus({ days: 1 }).startOf('day')
    return {
      from: fromDate,
      to: adjustedDateRange.to
    }
  }, [adjustedDateRange])

  const { data: models, isLoading: modelsLoading } = useModels()
  const { data: rowData, loading, error } = usePromoData(adjustedDateRange, models || [])
  const { dailyFanGainData, fanGainGGData, isLoading: fanGainLoading } = useFanGainData(
    fanGainDateRange,
    modelsLoading
  )

  const modelsMap = React.useMemo(() => {
    if (!models) return new Map()
    return new Map(models.map(model => [model.id, model.name]))
  }, [models])

  // Calculate internal fan gain for each model
  const calculatedFanGain = React.useMemo(() => {
    const results: { [model_id: string]: number } = {}

    if (fanGainLoading || !models || !adjustedDateRange?.from || !adjustedDateRange?.to) return results

    const fromDate = adjustedDateRange.from
    const toDate = adjustedDateRange.to

    // Filter to only normal models
    const normalModels = models.filter(model => model.account_type === 'Normal')

    normalModels.forEach(model => {
      const dailyModel = dailyFanGainData[model.id]
      const ggModel = fanGainGGData[model.id]

      let totalInternalFanGain = 0

      if (dailyModel) {
        // Calculate for each day in the range
        let currentDate = fromDate
        while (currentDate <= toDate) {
          const day = currentDate.day
          const dayKey = `day${day}`
          const nextDayKey = `day${day + 1}`

          // Get input value from daily fan gain data
          const inputValue = dailyModel.inputValues?.[dayKey] || 0

          // Calculate diff value (next day - current day from GG swaps)
          let diffValue = 0
          if (ggModel?.dailyValues) {
            const currentDayValue = ggModel.dailyValues[dayKey] || 0
            if (day === 31) {
              diffValue = 0
            } else {
              const nextDayValue = ggModel.dailyValues[nextDayKey]
              // Only calculate diff if next day data exists (undefined means no data)
              if (nextDayValue !== undefined) {
                diffValue = nextDayValue - currentDayValue
              }
            }
          }

          // Calculate internal fan gain (input - diff)
          const internalValue = inputValue - diffValue

          // Don't include value if next day is empty (prevents inaccurate large values)
          if (day < 31 && ggModel?.dailyValues) {
            const nextDayValue = ggModel.dailyValues[nextDayKey]
            if (nextDayValue === undefined) {
              currentDate = currentDate.plus({ days: 1 })
              continue
            }
          }

          totalInternalFanGain += Math.max(0, internalValue) // Don't count negative values

          currentDate = currentDate.plus({ days: 1 })
        }
      }

      results[model.id] = totalInternalFanGain
    })

    return results
  }, [models, dailyFanGainData, fanGainGGData, adjustedDateRange, fanGainLoading])

  // Check for incomplete data coverage
  const isDataIncomplete = React.useMemo(() => {
    if (!dateRange?.from || !dateRange?.to) return false

    // For now, we don't have model lists data to check completeness like the original component
    // This could be enhanced later if needed
    return false
  }, [dateRange])

  const formatDateRange = () => {
    if (!dateRange?.from || !dateRange?.to) return 'Select date range'

    if (dateRange.from.toMillis() === dateRange.to.toMillis()) {
      return formatDate(dateRange.from)
    }

    return `${formatDate(dateRange.from)} - ${formatDate(dateRange.to)}`
  }

  const handleConfirmDateRange = () => {
    setDateRange(tempDateRange)
    setIsPopoverOpen(false)
  }

  const handleCancelDateRange = () => {
    setTempDateRange(dateRange)
    setIsPopoverOpen(false)
  }

  const handlePopoverOpenChange = (open: boolean) => {
    setIsPopoverOpen(open)
    if (open) {
      setTempDateRange(dateRange)
    }
  }

  const enrichedRowData = React.useMemo((): EnrichedRowData[] => {
    if (!rowData || !models) return []

    // Group data by model_id to identify first sub group for each model
    const groupedByModel = rowData.reduce((acc, row) => {
      if (!acc[row.model_id]) {
        acc[row.model_id] = []
      }
      acc[row.model_id].push(row)
      return acc
    }, {} as { [model_id: string]: any[] })

    // Enrich the data with model names and fan gain calculations
    const enriched: EnrichedRowData[] = rowData.map(row => {
      const modelName = modelsMap.get(row.model_id) || row.model_id
      const fanGain = calculatedFanGain[row.model_id] || 0

      // Check if this is the first sub group for this model
      const isFirstSubGroup = groupedByModel[row.model_id]?.[0] === row

      return {
        ...row,
        modelName,
        fan_gain: isFirstSubGroup ? fanGain : 0,
        fan_gain_per_ad: isFirstSubGroup && row.int_promos > 0 ? parseFloat((fanGain / row.int_promos).toFixed(2)) : 0
      } as EnrichedRowData
    })

    // Group by mainGroup and sort each group by fan_gain_per_ad descending
    const groupedByMainGroup = enriched.reduce((acc, row) => {
      if (!acc[row.mainGroup]) {
        acc[row.mainGroup] = []
      }
      acc[row.mainGroup].push(row)
      return acc
    }, {} as { [mainGroup: string]: EnrichedRowData[] })

    // Define the desired group order
    const groupOrder = ['A', 'A1', 'A2', 'B', 'C', '1', '2']

    // Sort groups by the specified order, then sort each group by fan_gain_per_ad descending
    const sortedData = groupOrder
      .filter(groupName => groupedByMainGroup[groupName])
      .map((groupName: string) =>
        groupedByMainGroup[groupName].sort((a: EnrichedRowData, b: EnrichedRowData) => b.fan_gain_per_ad - a.fan_gain_per_ad)
      )
      .flat()

    // Add any groups not in the predefined order at the end
    const remainingGroups = Object.keys(groupedByMainGroup)
      .filter(groupName => !groupOrder.includes(groupName))
      .sort()

    const remainingData = remainingGroups
      .map((groupName: string) =>
        groupedByMainGroup[groupName].sort((a: EnrichedRowData, b: EnrichedRowData) => b.fan_gain_per_ad - a.fan_gain_per_ad)
      )
      .flat()

    const finalSortedData = [...sortedData, ...remainingData]

    return finalSortedData
  }, [rowData, modelsMap, models, calculatedFanGain])

  const columnDefs: ColDef[] = [
    {
      field: 'mainGroup',
      headerName: 'Main Group',
      rowGroup: true,
      hide: true,
    },
    {
      field: 'modelName',
      headerName: 'Model',
      rowGroup: true,
      hide: true,
    },
    {
      field: 'int_promos',
      headerName: 'INT Promo',
      type: 'numericColumn',
      width: 160,
      aggFunc: 'sum',
      valueFormatter: (params: any) => {
        // Hide values for main group rows (level 0)
        if (params.node.group && params.node.level === 0) return ''
        return params.value === 0 ? '' : params.value
      },
      suppressHeaderFilterButton: true,
      suppressHeaderMenuButton: true
    },
    {
      field: 'cross_promos',
      headerName: 'CROSS Promo',
      type: 'numericColumn',
      width: 160,
      aggFunc: 'sum',
      valueFormatter: (params: any) => {
        // Hide values for main group rows (level 0)
        if (params.node.group && params.node.level === 0) return ''
        return params.value === 0 ? '' : params.value
      },
      suppressHeaderFilterButton: true,
      suppressHeaderMenuButton: true
    },
    {
      field: 'total',
      headerName: 'Total',
      type: 'numericColumn',
      width: 120,
      aggFunc: 'sum',
      valueFormatter: (params: any) => {
        // Hide values for main group rows (level 0)
        if (params.node.group && params.node.level === 0) return ''
        return params.value === 0 ? '' : params.value
      },
      suppressHeaderFilterButton: true,
      suppressHeaderMenuButton: true
    },
    {
      field: 'fan_gain',
      headerName: 'FG',
      type: 'numericColumn',
      width: 120,
      aggFunc: 'sum',
      valueFormatter: (params: any) => {
        // Hide values for main group rows (level 0) only
        if (params.node.group && params.node.level === 0) return ''
        return params.value === 0 ? '' : params.value
      },
      suppressHeaderFilterButton: true,
      suppressHeaderMenuButton: true
    },
    {
      field: 'fan_gain_per_ad',
      headerName: 'FG / Ad',
      type: 'numericColumn',
      width: 150,
      valueGetter: (params: any) => {
        // For group nodes, calculate FG/Ad from aggregated values
        if (params.node.group && params.node.level === 1) {
          const totalFanGain = params.node.aggData?.fan_gain || 0
          const totalIntPromos = params.node.aggData?.int_promos || 0
          return totalIntPromos > 0 ? (totalFanGain / totalIntPromos).toFixed(2) : 0
        }
        // For leaf nodes, return the original value
        return params.data?.fan_gain_per_ad || 0
      },
      valueFormatter: (params: any) => {
        // Hide values for main group rows (level 0) only
        if (params.node.group && params.node.level === 0) return ''
        return params.value === 0 ? '' : params.value
      },
      suppressHeaderFilterButton: true,
      suppressHeaderMenuButton: true
    }
  ]

  if (loading || modelsLoading || fanGainLoading) {
    return (
      <div className="flex flex-col flex-1 items-center justify-center" style={{ height: 'calc(100vh - 100px)' }}>
        <div>Loading promo and fan gain data...</div>
      </div>
    )
  }

  if (error) {
    return (
      <div className="flex flex-col flex-1 items-center justify-center" style={{ height: 'calc(100vh - 100px)' }}>
        <div className="text-brand-red">Error loading promo data: {error}</div>
      </div>
    )
  }

  return (
    <div className="space-y-4">
      {/* Date Range Selector */}
      <div className="flex items-center gap-4">
        <div className="flex items-center gap-2">
          <Label className="text-sm font-medium">Date Range:</Label>
          <Popover open={isPopoverOpen} onOpenChange={handlePopoverOpenChange}>
            <PopoverTrigger asChild>
              <Button
                variant="outline"
                className={cn(
                  "w-[280px] justify-start text-left font-normal",
                  !dateRange?.from && "text-muted-foreground"
                )}
              >
                <CalendarIcon className="mr-2 h-4 w-4" />
                {formatDateRange()}
              </Button>
            </PopoverTrigger>
            <PopoverContent className="w-auto p-0" align="start">
              <div className="p-0">
                <Calendar
                  mode="range"
                  defaultMonth={dateRange?.from.toJSDate()}
                  selected={{ from: tempDateRange?.from.toJSDate(), to: tempDateRange?.to.toJSDate() }}
                  onSelect={(range) => {
                    if (range?.from && range?.to) {
                      setTempDateRange({ from: DateTime.fromJSDate(range?.from, { zone: 'UTC' }), to: DateTime.fromJSDate(range?.to, { zone: 'UTC' }) })
                    }
                  }}
                  numberOfMonths={2}
                />
                <div className="flex items-center justify-end gap-2 p-3 border-t">
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={handleCancelDateRange}
                  >
                    Cancel
                  </Button>
                  <Button
                    size="sm"
                    onClick={handleConfirmDateRange}
                    disabled={!tempDateRange?.from || !tempDateRange?.to}
                  >
                    Apply
                  </Button>
                </div>
              </div>
            </PopoverContent>
          </Popover>
        </div>

        {/* Warning for incomplete data */}
        {!loading && !fanGainLoading && isDataIncomplete && (
          <Tooltip delayDuration={0}>
            <TooltipTrigger asChild>
              <AlertTriangleIcon className="h-4 w-4 text-brand-gold cursor-help" />
            </TooltipTrigger>
            <TooltipContent>
              <p>Missing some model lists in the given date range, Fan Gain Per Ad values may be inflated.</p>
            </TooltipContent>
          </Tooltip>
        )}
      </div>

      {/* Data Grid */}
      <div className="flex flex-col flex-1" style={{ height: 'calc(100vh - 200px)' }}>
        <DataGrid
          columnDefs={columnDefs}
          rowData={enrichedRowData}
          height="100%"
          className="min-h-[400px]"
          gridOptions={{
            defaultColDef: {
              flex: 1,
              minWidth: 100,
              filter: 'agTextColumnFilter',
              floatingFilter: true,
            },
            autoGroupColumnDef: {
              headerName: 'Sub Group',
              field: 'group_name',
              minWidth: 300,
              cellRenderer: "agGroupCellRenderer",
            },
            suppressAggFuncInHeader: true,
            groupDefaultExpanded: 1,
            getRowStyle: (params: RowClassParams) => {
              if (params.node.group && params.node.level === 1) {
                return { backgroundColor: '#ecececff' };
              }
              return undefined;
            }
          }}
          animateRows={true}
        />
      </div>
    </div>
  )
}